import type { BranchHeadCommitMeta } from "~/branching/model/branch-head-commit-meta";

/** Revision fields needed for head-commit enrichment; intentionally narrower than the full SDK revision DTO. */
export type BranchHeadCommitMetaRevisionSource = {
  id: string;
  created: string | Date;
  createdById?: string | null;
};

/**
 * Maps a tag's revision history to its head-commit enrichment, selecting the newest revision by
 * `created`. Selection is order-independent: real `getTagHistory` ordering is undocumented, so we
 * never rely on array position. Returns `null` when there is no history; the caller decides whether
 * that is a not-found.
 */
export function toBranchHeadCommitMeta(revisions: BranchHeadCommitMetaRevisionSource[]): BranchHeadCommitMeta | null {
  if (revisions.length === 0) return null;

  const latest = revisions.reduce((newest, revision) =>
    revisionTime(revision) > revisionTime(newest) ? revision : newest,
  );

  return {
    updatedAt: new Date(revisionTime(latest)),
    authorId: latest.createdById ?? null,
    headCommitRevisionId: latest.id,
  };
}

function revisionTime(revision: BranchHeadCommitMetaRevisionSource): number {
  const time = new Date(revision.created).getTime();
  if (!Number.isFinite(time)) {
    throw new Error(`Invalid created date on revision ${revision.id}: ${String(revision.created)}`);
  }
  return time;
}
